<template>
  <section class="traffic-source">
    <TableCard
      ref="datatable"
      v-model:sort="sorting"
      v-model:page="page"
      v-model:page-size="pageSize"
      :loading="loading"
      :columns="columns"
      :sort-by-name="sortByName"
      :sort-dir-name="sortDirName"
      :rows="rows"
      :count="trafficSource.totalRows"
      :formaters="formaters"
      :actions="actions"
      :header="$t('trafficSources.title')"
      dont-fill-empty
      extend-height
      i18n-path="reports"
      @fetch-data="fetchDataTable"
    >
      <template v-slot:actionsPrepend>
        <div class="ui-mobile-12 ui-g-md">
          <Tooltip
            class="tooltip-btn-disabled"
            :title="$t('tooltip_limits.trafficSources')"
            placement="bottom-left"
            :disabled="!addDisabled"
          >
            <button
              class="btn btn-primary btn-sm ui-mobile-12"
              :disabled="addDisabled"
              @click="addNewTrafficSource"
            >
              + {{ $t('trafficSources.newTrafficSource') }}
            </button>
          </Tooltip>
        </div>
      </template>
    </TableCard>

    <AddTrafficSource
      ref="addNewTrafficSource"
      :offset-params="queryParams"
    />
    <EditTrafficSource
      ref="editTrafficSource"
      :offset-params="queryParams"
    />
  </section>
</template>

<script>
import Formaters from '@/views/Dashboard/CustomReports/formaters';
import InjectPlugins from '@/components/Report/Components/inject-plugins';
import UrlMixin from '@/components/Query/url-mixin';
import UrlMixinHooks from '@/components/Query/url-mixin-hooks';
import {
  pageSizeMixin,
  resolvePageSize,
} from '@/views/Dashboard/Reports/page_size';
import TableCard from '@/components/Cards/TableCard.vue';
import HelpersMixin from '@/components/Helpers/helpers-mixin';
import tableFlowMixinGenerator from '@/views/Dashboard/Reports/tableFlowMixin';
import Tooltip from '@/components/Tooltip.vue';
import trafficSourceState from '@/store/traffic-source/state';
import promocodeState from '@/store/promocode/state';
import UiTableAction from '@/components/ui/UiTableAction.vue';
import UiTablePopoverAction from '@/components/ui/UiTablePopoverAction.vue';
import AddTrafficSource from './TrafficSources/AddTrafficSource.vue';
import EditTrafficSource from './TrafficSources/EditTrafficSource.vue';

const sizes = {
  1080: 20,
  1440: 30,
};
const viewName = '/traffic-source';
const pageLimitProp = 'pageSize';
const pageSize = resolvePageSize(viewName, {
  _default: 15,
  sizes,
});

const defaultParams = {
  page: 1,
  pageSize,
  sort_column: 'created_at',
  sort_dir: 'desc',
};

const fetch = (ctx, params) => {
  if (ctx.$_.isEmpty(params)) {
    params = {
      ...defaultParams,
    };
  }

  const count = () => ctx.$store.dispatch('getTrafficSourceCount');
  const promocodeOffers = () => ctx.$store.dispatch('getPromocodeOffers');
  const trafficSources = () => ctx.$store.dispatch('getTrafficSources', {
    offset: params.offset || 0,
    sort_dir: 'desc',
    sort_column: params.sort_column || 'created_at',
    ...params,
    limit: ctx.pageSize || pageSize,
  });

  return Promise.all([count(), trafficSources(), promocodeOffers()]);
};

const tableFlowMixin = tableFlowMixinGenerator({
  defaultParams: {
    ...defaultParams,
  },
  params: Object.keys(defaultParams),
  fetch,
  windowRefreshHook: true,
  sortByName: 'sort_column',
  sortDirName: 'sort_dir',
});

export default {
  name: 'TrafficSource',
  mixins: [
    UrlMixin,
    UrlMixinHooks,
    InjectPlugins,
    pageSizeMixin,
    HelpersMixin,
    tableFlowMixin,
  ],
  fetch,
  components: {
    AddTrafficSource,
    EditTrafficSource,
    TableCard,
    Tooltip,
  },
  props: ['fetchedData', 'passedParams'],
  data() {
    return {
      viewName,
      pageLimitProp,
      pagination: 'true',
      columns: [
        {
          name: 'created_at',
          align: 'left',
          sortable: 'custom',
          filterable: false,
          width: 155,
          format: 'date-time',
        },
        {
          name: 'name_ts',
          align: 'left',
          sortable: false,
          filterable: false,
        },
        {
          name: 'postbacks',
          align: 'center',
          sortable: false,
          filterable: false,
          width: 100,
        },
      ],
      listener: '',
      lastQuery: {},
      formaters: Formaters,
      isTableReady: false,
      // vue3-migration -- убираем перерисовку в фоне для keep-alive
      trafficSource: trafficSourceState(),
      promocode: promocodeState(),
      routePath: this.$route.path,
    };
  },
  computed: {
    isEmptyOffers() {
      return this.$_.isEmpty(this.$store.state.promocode.offers);
    },
    actions() {
      const operations = [
        {
          name: 'tagManager',
          componentAlt: {
            component: UiTableAction,
            props: (row) => {
              const link = `/dashboard/traffic-source/tag-manager/${
                row.data.id
              }/${this.compressUrlQuery(row.data.id)}`;

              return {
                tooltipContent: this.$t('tagManager.card.tagManager'),
                iconClass: 'far fa-tag',
                colorPreset: 'info',
                link: `#${link}`,
                onClick: (e) => {
                  e.preventDefault();
                  localStorage.setItem(
                    'getBackUrlTrafficSource',
                    JSON.stringify(this.routePath),
                  );

                  this.$router.push(link);
                },
              };
            },
          },
        },
        {
          name: 'postbacks',
          componentAlt: {
            component: UiTableAction,
            props: (row) => {
              const link = `/dashboard/traffic-source/postbacks/${
                row.data.id
              }/${this.compressUrlQuery(row.data.id)}`;

              return {
                tooltipContent: this.$t('postbacks.mainTitle'),
                iconClass: 'far fa-link',
                colorPreset: 'info',
                link: `#${link}`,
                onClick: (e) => {
                  e.preventDefault();
                  localStorage.setItem(
                    'getBackUrlTrafficSource',
                    JSON.stringify(this.routePath),
                  );

                  this.$router.push(link);
                },
              };
            },
          },
        },
        {
          name: 'edit',
          componentAlt: {
            component: UiTableAction,
            props: (row) => {
              const {
                data: { id },
              } = row;

              return {
                tooltipContent: this.$t('postbacks.card.edit'),
                iconClass: 'far fa-pen',
                colorPreset: 'green',
                onClick: (e) => {
                  e.cancelBubble = true;
                  this.editTrafficSource(id);
                },
              };
            },
          },
        },
        {
          name: 'delete',
          componentAlt: {
            component: UiTablePopoverAction,
            props: (row) => ({
              tooltipContent: this.$t('postbacks.card.delete'),
              iconClass: 'far fa-trash-alt',
              colorPreset: 'red',
              width: 250,
              message: this.$t('trafficSources.warning.warningText'),
              buttons: [
                {
                  text: this.$t('mediaBuying.channels.cancel'),
                },
                {
                  text: this.$t('postbacks.card.delete'),
                  type: 'danger',
                  click: () => this.deleteSource(row.data.id),
                },
              ],
            }),
          },
        },
      ];

      return {
        width: 140,
        name: () => this.$t('profile.apiKeys.table.actions'),
        operations: operations.filter((e) => {
          return !(e.name === 'postbacks' && this.isEmptyOffers);
        }),
      };
    },
    rows() {
      return this.trafficSource.list.map((e) => ({
        data: e.data,
        created_at: e.created_at,
        name_ts: e.name_ts,
        postbacks: this.formatType(e.postbacks),
      }));
    },
    addDisabled() {
      const { max_traffic_sources } = this.$store.state.settings.limitations;

      return max_traffic_sources !== null && this.$store.state.trafficSource.totalRows >= max_traffic_sources;
    },
    computedTrafficSourceStoreParams() {
      return {
        activated: this.isComponentActivated,
        trafficSource: this.$store.state.trafficSource,
      };
    },
    computedPromocodeStoreParams() {
      return {
        activated: this.isComponentActivated,
        promocode: this.$store.state.promocode,
      };
    },
    computedRoutePath() {
      return {
        activated: this.isComponentActivated,
        routePath: this.$route.path,
      };
    },
  },
  watch: {
    computedTrafficSourceStoreParams: {
      immediate: true,
      deep: true,
      handler({activated, trafficSource}) {
        if (activated) {
          this.trafficSource = this.$_.cloneDeep(trafficSource);
        }
      },
    },
    computedPromocodeStoreParams: {
      immediate: true,
      deep: true,
      handler({activated, promocode}) {
        if (activated) {
          this.promocode = this.$_.cloneDeep(promocode);
        }
      },
    },
    computedRoutePath: {
      immediate: true,
      handler({activated, routePath}) {
        if (activated) {
          this.routePath = {
            name: this.$route.name,
            params: this.$route.params,
            query: this.$route.query,
          };
        }
      },
    },
  },
  methods: {
    formatType(type) {
      return isNaN(type) ? this.$t('paymentHistory.no') : type;
    },
    deleteSource(id) {
      this.$store.dispatch('deleteTrafficSource', id).then(() => {
        if (this.rows.length === 1 && this.page > 1) {
          this.page = this.page - 1;
        }

        this.$store.dispatch('getTrafficSourcesLazy', this.queryParams);
        this.$alert.success(this.$t('trafficSources.postbacks.trafficDeleted'));
      });
    },
    addNewTrafficSource(id) {
      if (!this.addDisabled) {
        this.$refs.addNewTrafficSource.open(id);
      }
    },
    editTrafficSource(id) {
      this.$refs.editTrafficSource.open(id);
    },
    clearTooltip() {
      // vue3-migration -- глобальный безконтрльный listener 
      // document.addEventListener(
      //   'mouseup',
      //   (this.listener = () => {
      //     setTimeout(() => {
      //       const tooltip = document.querySelectorAll('.el-tooltip__popper');
      //       tooltip.forEach((e, i) => {
      //         tooltip[i].style.display = 'none';
      //       });
      //     }, 500);
      //   }),
      // );
    },
  },
  created() {
    this.setFromQuery(this.passedParams);
    this.actions.operations.forEach(
      (o) => o.component && (o.component = o.component.bind(this)),
    );
  },
  beforeMount() {
    this.pageSize = this.limit;
  },
  mounted() {
    this.clearTooltip();
  },
  activated() {
    this.clearTooltip();
  },
};
</script>

<style lang="scss">
.traffic-source {
  min-height: calc(100vh - 100px);

  > .card {
    min-height: calc(100vh - 100px);
  }

  &__title {
    font-size: 16px;
    font-weight: 600;
  }

  // .head-wrap {
  //   display: flex;
  //   justify-content: space-between;
  //   align-items: center;
  // }

  @media (max-width: 650px) {
    > .card {
      padding: 0 !important;
    }

    .mob-wrap {
      .castom-margin-bottom:first-child {
        border-top: 1px solid rgba(0, 0, 0, 0.125);
      }

      .actions a:last-child,
      .btn.text-green {
        border-left: 1px solid #e7ecf1;
      }
    }

    // .head-wrap {
    //   padding: 6px 16px 16px;
    //   flex-direction: column;
    //   align-items: baseline;

    //   .btn-primary {
    //     margin: 0 !important;
    //     width: 100%;
    //   }

    //   > .d-flex {
    //     display: block !important;
    //     width: 100%;
    //     margin-top: 10px;
    //   }

    //   .the-pagination {
    //     margin-top: 10px;
    //   }
    // }
  }
}
</style>
